import z, { string } from "zod";

import { basicValidator } from "@/lib/validators";

const createService = z.object({
  id: z.string().uuid().optional(),
  itemId: z.string().uuid({
    message: "Required",
  }),
  quantity: z.coerce
    .number({
      message: "Required",
      required_error: "Required",
      invalid_type_error: "Workers number must be a number",
    })
    .min(1, {
      message: "Qty must be greater than 0",
    }),
});

const updateService = z.object({
  id: string().uuid(),
  itemId: z.string().uuid().optional(),
  quantity: z.coerce.number().optional(),
});

export const baseDocketReportSchema = z.object({
  docketId: z.coerce.number().optional(),
  customerId: z.string().uuid(),
  projectId: z.string().uuid(),
  report: z.string(),
  deleteServices: z.array(z.string()).optional(),
  services: z.array(createService),
  forecastedCost: z.number().optional(),
  purchaseOrder: basicValidator(false),
  workersNo: z.coerce
    .number({
      message: "Required",
    })
    .max(9999, { message: "Must be up to 4 digits" })
    .optional(), // Max value of 9999 ensures a max of 4 digits,
  siteManager: basicValidator(false),
  status: z.string().optional(),
  jobDescription: basicValidator(false, "", 0, 500),
  images: z
    .array(
      z
        .instanceof(File, {
          message: "Required",
        })
        .or(z.string())
        .optional(),
    )
    .optional(),
  declaration: basicValidator(false),
});

const projectManagerOnSiteSchema = z.discriminatedUnion(
  "isProjectManagerOnSite",
  [
    z.object({
      isProjectManagerOnSite: z.literal(true),
      signature: z
        .string({ message: "Signature is required" })
        .min(1, "Signature is required"),
    }),
    z.object({
      isProjectManagerOnSite: z.literal(false),
    }),
  ],
);

const disputeSchema = z.discriminatedUnion("isDispute", [
  z.object({
    isDispute: z.literal(false),
  }),
  z.object({
    isDispute: z.literal(true),
    disputeName: z.string().min(2, {
      message: "Required",
    }),
    disputeReason: z.string().min(1, {
      message: "Required",
    }),
    disputeImage: z
      .array(
        z
          .instanceof(File, {
            message: "Required",
          })
          .or(z.string()),
      )
      .min(1, {
        message: "Required",
      }),
  }),
]);

export const createDocketReportSchema = baseDocketReportSchema
  .and(disputeSchema)
  .and(projectManagerOnSiteSchema);

export const updateDocketReportSchema = baseDocketReportSchema
  .partial()
  .omit({ services: true })
  .extend({
    deleteServices: z.array(z.string()).optional(),
    services: z.array(updateService.or(createService)).optional(),
  })
  .and(disputeSchema.optional())
  .and(projectManagerOnSiteSchema.optional());

export const queryDocketSchema = z
  .object({
    search: z.string(),
    customerId: z.string(),
    projectId: z.string(),
  })
  .partial();

export type TCreateService = z.infer<typeof createService>;
export type TUpdateService = z.infer<typeof updateService>;
export type TCreateDocketReport = z.infer<typeof createDocketReportSchema>;
export type TUpdateDocketReport = z.infer<typeof updateDocketReportSchema>;
export type TDocketQuery = z.infer<typeof queryDocketSchema>;
